/**
 * Authentication Context
 * Manages login state, session, and role-based access control
 *
 * Updated to use new types aligned with DB schema
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { UserRole } from '@/types/user.types'
import {
  AuthContextType,
  AuthSession,
  AuthUser,
  INITIAL_AUTH_SESSION,
  AUTH_STORAGE_KEYS,
} from '@/types/auth.types'
import { renewPlatformToken } from '@/services/legalControl/platformMfa'
import { decodeJwtClaims } from '@/services/auth/mappers'
import { getAuthService } from '@/services/auth'
import { createHttpAuthService } from '@/services/auth/auth.http'
import {
  loginClient,
  validateVaultTokens,
  isIndependentToken,
  type ClientTokens,
} from '@/services/vault/auth'
import { apiClient } from '@/services/http/client'
import { clearAccountData } from '@/security/clearAccountData'
import { normalizeRole } from '@/security/accessContract'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession>(INITIAL_AUTH_SESSION)

  // Initialize session from localStorage/sessionStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const isRemember = localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'
      const storage = isRemember ? localStorage : sessionStorage
      const storedToken =
        storage.getItem(AUTH_STORAGE_KEYS.TOKEN) || localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
      const storedUser =
        storage.getItem(AUTH_STORAGE_KEYS.USER) || localStorage.getItem(AUTH_STORAGE_KEYS.USER)
      const storedRefreshToken =
        storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) ||
        localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
      const storedSessionId =
        storage.getItem(AUTH_STORAGE_KEYS.SESSION_ID) ||
        localStorage.getItem(AUTH_STORAGE_KEYS.SESSION_ID)

      if (storedToken && storedUser) {
        try {
          const user = JSON.parse(storedUser) as AuthUser
          // Validate token
          const validatedUser = await (
            isIndependentToken(storedToken) ? createHttpAuthService() : getAuthService()
          ).validateToken(storedToken)

          if (storage.getItem(AUTH_STORAGE_KEYS.TOKEN) !== storedToken) return
          if (validatedUser) {
            setSession({
              user: {
                ...validatedUser,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                fullName: user.fullName,
              },
              token: storedToken,
              refreshToken: storedRefreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              sessionId: storedSessionId,
            })
          } else {
            // Token invalid, clear storage
            clearStoredAuth()
            setSession({
              ...INITIAL_AUTH_SESSION,
              isLoading: false,
            })
          }
        } catch {
          clearStoredAuth()
          setSession({
            ...INITIAL_AUTH_SESSION,
            isLoading: false,
            error: 'Failed to restore session',
          })
        }
      } else {
        setSession(prev => ({ ...prev, isLoading: false }))
      }
    }

    initializeAuth()
  }, [])

  useEffect(() => {
    const syncAuthority = async () => {
      const storage =
        localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'
          ? localStorage
          : sessionStorage
      const token = storage.getItem(AUTH_STORAGE_KEYS.TOKEN)
      if (!token) return
      clearAccountData()
      const verified = await (
        isIndependentToken(token) ? createHttpAuthService() : getAuthService()
      ).validateToken(token)
      if (storage.getItem(AUTH_STORAGE_KEYS.TOKEN) !== token) return
      if (!verified) {
        clearStoredAuth()
        setSession({ ...INITIAL_AUTH_SESSION, isLoading: false })
        return
      }
      setSession(previous => {
        const user = {
          ...verified,
          email: previous.user?.email ?? '',
          fullName: previous.user?.fullName ?? verified.fullName,
          firstName: previous.user?.firstName ?? verified.firstName,
        }
        storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
        return { ...previous, user, token }
      })
    }
    window.addEventListener('taxhive:tokens-refreshed', syncAuthority)
    return () => window.removeEventListener('taxhive:tokens-refreshed', syncAuthority)
  }, [])

  /**
   * Clear all stored auth data
   */
  const clearStoredAuth = () => {
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER)
    localStorage.removeItem(AUTH_STORAGE_KEYS.SESSION_ID)
    localStorage.removeItem(AUTH_STORAGE_KEYS.REMEMBER_ME)
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER)
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.SESSION_ID)
  }

  /**
   * Store auth data in localStorage or sessionStorage depending on rememberMe
   */
  const storeAuth = (
    token: string,
    refreshToken: string,
    user: AuthUser,
    sessionId: string,
    rememberMe?: boolean,
    clearData = true
  ) => {
    if (clearData) clearAccountData()
    clearStoredAuth()
    const isRemember = Boolean(rememberMe)
    const storage = isRemember ? localStorage : sessionStorage
    storage.setItem(AUTH_STORAGE_KEYS.TOKEN, token)
    storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
    storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
    storage.setItem(AUTH_STORAGE_KEYS.SESSION_ID, sessionId)
    localStorage.setItem(AUTH_STORAGE_KEYS.REMEMBER_ME, String(isRemember))
  }

  /**
   * Login with email and password
   */
  const login = async (
    email: string,
    password: string,
    rememberMe?: boolean,
    mfaCode?: string,
    independent = false
  ): Promise<void> => {
    setSession(prev => ({ ...prev, isLoading: true, error: null }))

    let result
    try {
      // The service handles credential checks (and, in mock mode, audit logging).
      result = independent
        ? await loginClient(email, password, mfaCode)
        : await getAuthService().login(email, password, mfaCode, rememberMe)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid email or password'
      setSession({
        ...INITIAL_AUTH_SESSION,
        isLoading: false,
        error: message,
      })
      throw err
    }

    const { user, token, refreshToken, sessionId } = result

    // Store in storage
    storeAuth(token, refreshToken, user, sessionId, rememberMe)

    setSession({
      user: { ...user, currentSessionId: sessionId },
      token,
      refreshToken,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      sessionId,
    })
  }

  /**
   * Logout - optionally from all devices
   */
  const logout = async (allDevices?: boolean): Promise<void> => {
    const currentUser = session.user
    const currentSessionId = session.sessionId

    if (currentUser?.authScope === 'client_vault') {
      try {
        await apiClient.post('/auth/client/logout')
      } catch {
        /* Clear local state even if session has already ended. */
      }
    } else
      await getAuthService().logout({
        user: currentUser,
        sessionId: currentSessionId,
        allDevices,
      })

    clearAccountData()
    clearStoredAuth()
    setSession({
      ...INITIAL_AUTH_SESSION,
      isLoading: false,
    })
    window.location.assign('/login')
  }

  /**
   * Refresh the current session
   */
  const refreshSession = async (): Promise<void> => {
    const isRemember = localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'
    const storage = isRemember ? localStorage : sessionStorage
    const storedRefreshToken =
      storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) ||
      localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)

    if (!storedRefreshToken) {
      throw new Error('No refresh token available')
    }

    const priorToken = storage.getItem(AUTH_STORAGE_KEYS.TOKEN)
    const { token: newToken, refreshToken: newRefreshToken } = await (
      session.user?.authScope === 'client_vault' ? createHttpAuthService() : getAuthService()
    ).refresh(storedRefreshToken)

    if (session.user) {
      const freshUser = await (
        session.user.authScope === 'client_vault' ? createHttpAuthService() : getAuthService()
      ).validateToken(newToken)
      if (storage.getItem(AUTH_STORAGE_KEYS.TOKEN) !== priorToken)
        throw new Error('Account changed during refresh')
      if (!freshUser) throw new Error('Session authority could not be refreshed')
      const user = {
        ...freshUser,
        email: session.user.email,
        fullName: session.user.fullName,
        firstName: session.user.firstName,
        lastName: session.user.lastName,
      }
      clearAccountData()
      storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
      storage.setItem(AUTH_STORAGE_KEYS.TOKEN, newToken)
      storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)

      setSession(prev => ({
        ...prev,
        user,
        token: newToken,
        refreshToken: newRefreshToken,
      }))
    }
  }

  /**
   * Check if user has any of the specified roles
   */
  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!session.user) return false

    const roleArray = Array.isArray(roles) ? roles : [roles]
    const effectiveRole = normalizeRole(session.user.role)
    return roleArray.some(role => normalizeRole(role) === effectiveRole)
  }

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    if (!session.user) return false
    const permissions = session.user.permissions ?? []
    return (
      permissions.includes(permission) ||
      permissions.some(item => {
        return item.endsWith('.*') && permission.startsWith(item.slice(0, -1))
      })
    )
  }

  /**
   * Check if user can access a feature (alias for hasPermission)
   */
  const canAccessFeature = (feature: string): boolean => {
    if (!session.user) return false

    const required = feature.toLowerCase()
    return (
      (session.user.features ?? []).some(
        grant => grant.toLowerCase() === required || grant.toLowerCase().startsWith(`${required}.`)
      ) || hasPermission(feature)
    )
  }

  const adoptTokens = async (data: ClientTokens, email?: string) => {
    const priorToken =
      sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) ||
      localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
    const result = await validateVaultTokens(data, email ?? session.user?.email)
    if (
      (sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) ||
        localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)) !== priorToken
    )
      throw new Error('Account changed during verification')
    const refreshToken = result.refreshToken || session.refreshToken || ''
    const sameAccount =
      session.user?.id === result.user.id && session.user.authScope === result.user.authScope
    storeAuth(
      result.token,
      refreshToken,
      result.user,
      result.sessionId,
      localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true',
      !sameAccount
    )
    setSession({
      user: result.user,
      token: result.token,
      refreshToken,
      sessionId: result.sessionId,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    })
  }

  const renewPlatformMfa = async (code: string) => {
    if (session.user?.authScope !== 'platform' || !session.token) throw new Error('Platform session required')
    const prior = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) || localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
    if (prior !== session.token) throw new Error('Account changed during verification')
    const token = await renewPlatformToken(prior, code)
    const verified = await createHttpAuthService().validateToken(token)
    if (!verified || verified.id !== session.user.id || verified.authScope !== 'platform' ||
        (sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) || localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)) !== prior)
      throw new Error('Account changed during verification')
    const user = { ...verified, email: session.user.email, firstName: session.user.firstName, lastName: session.user.lastName, fullName: session.user.fullName }
    const sessionId = String(decodeJwtClaims(token)?.session_id ?? '')
    storeAuth(token, session.refreshToken || '', user, sessionId,
      localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true', false)
    setSession({ ...session, user, token, sessionId })
  }

  const value: AuthContextType = {
    session,
    login,
    logout,
    isLoggedIn: session.isAuthenticated,
    user: session.user,
    hasRole,
    hasPermission,
    canAccessFeature,
    refreshSession,
    renewPlatformMfa,
    adoptTokens,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
